import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import StatusChip from "../common/StatusChip";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface PurchaseOrderDetailsTabProps {
  order: PurchaseOrderResponseDto;
}

const PurchaseOrderDetailsTab: React.FC<PurchaseOrderDetailsTabProps> = ({
  order,
}) => {
  const { header, items, payments, charges } = order;
  const currency = "KES";

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Supplier Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Supplier Name
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: "500" }}
              >
                {order.supplierName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mt: 2 }}
              >
                Expected Delivery Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "500" }}>
                {order.expectedDate
                  ? new Date(order.expectedDate).toLocaleString()
                  : "Not Confirmed"}
              </Typography>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PIN: {header?.companySnapshot?.taxId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.phone} |{" "}
                {header?.companySnapshot?.email}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Purchase Order Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">Ordered</TableCell>
                    <TableCell align="center">Received</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.brand} | {item.category}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {item.orderedQuantity}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color:
                            item.receivedQuantity < item.orderedQuantity
                              ? "warning.main"
                              : "inherit",
                        }}
                      >
                        {item.receivedQuantity}
                      </TableCell>
                      <TableCell align="right">
                        {formatPlainNumber(item.unitCost)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatPlainNumber(item.totalCost)}
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip status={item.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Breakdown
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: "bold", mt: 2 }}
                >
                  Payment History
                </Typography>
                {payments && payments.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {payment.paymentMethodName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {new Date(payment.createdAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "none",
                                pr: 0,
                                fontWeight: "bold",
                                color: "success.main",
                              }}
                            >
                              {formatPlainNumber(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No payments recorded yet.
                  </Typography>
                )}

                {/* --- ADDED CHARGES SECTION --- */}
                {charges && charges.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px dashed rgba(255,255,255,0.1)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: "bold" }}
                    >
                      Supplier Charges
                    </Typography>
                    {charges.map((charge, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {charge.name} ({charge.rate}% - {charge.chargeType})
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(charge.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {currency}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    mt: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Order Total
                  </Typography>
                  <Typography
                    variant="h6"
                    color="secondary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {currency} {formatPlainNumber(order.total)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(order.paidAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color:
                        order.balanceAmount > 0 ? "error.main" : "secondary",
                    }}
                  >
                    {formatPlainNumber(order.balanceAmount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PurchaseOrderDetailsTab;
